import React, { useEffect, useMemo, useState } from "react";
import { api, toastError } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { toast } from "sonner";
import { HeadersMappingTable } from "../components/HeadersMappingTable";
import { useFieldDictionary } from "../hooks/useFieldDictionary";
import {
  duplicateTargets,
  unmappedRequired,
  parseMappingError,
  IGNORE_TARGET,
} from "../lib/mappingValidation";
import { normalizeAnalysis } from "../lib/analysisContract";
import { AlertTriangle, RefreshCw } from "lucide-react";

type JobShape = {
  id: string;
  status: string;
  version: number;
  analysis?: unknown;
  mapping?: Record<string, string>;
};

/**
 * Step 3 — Map columns.
 *
 * Rendered when `job.status ∈ {ANALYZING, MAPPED}`. Reads
 * `job.analysis.headers`, `job.analysis.sample`, `job.analysis.suggested_mapping`
 * to seed the table, then persists mapping via
 * `POST /api/import/jobs/:id/mapping` with `if_version`. On the first mount
 * (or when the job carries no user mapping yet) we seed from the backend's
 * suggested mapping, dropping nulls so the user is presented with an editable
 * best-guess.
 *
 * 409 version_conflict: the wizard shell's refetch is called so the user sees
 * the up-to-date job, and an inline "This job changed elsewhere" banner is
 * shown until they re-attempt.
 */
export function Step3Mapping({
  job,
  onJobUpdated,
}: {
  job: JobShape;
  onJobUpdated: () => void;
}) {
  const { fields, error: fieldsError } = useFieldDictionary();
  const analysis = useMemo(() => normalizeAnalysis(job), [job]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const headers = analysis.headers;
  const sampleRows = analysis.sampleRows;
  const suggested = analysis.suggestedMapping;

  // Seed mapping from the job. Priority: saved mapping > suggested mapping.
  // "dirty" tracks whether the in-memory mapping differs from what the
  // server actually persisted (`job.mapping`). When the job carries no
  // saved mapping yet (ANALYZING), seeding from suggestions counts as
  // dirty so the Save button becomes enabled without a spurious edit.
  useEffect(() => {
    const savedKeys = job.mapping ? Object.keys(job.mapping).length : 0;
    const seed: Record<string, string> = {};
    if (savedKeys > 0) {
      for (const h of headers) seed[h] = job.mapping![h] ?? IGNORE_TARGET;
      setDirty(false);
    } else {
      for (const h of headers) {
        const sug = suggested[h];
        seed[h] = sug || IGNORE_TARGET;
      }
      setDirty(Object.values(seed).some((v) => v && v !== IGNORE_TARGET));
    }
    setMapping(seed);
  }, [job.id, job.version]);

  const dupes = useMemo(() => duplicateTargets(mapping), [mapping]);
  const missingRequired = useMemo(
    () => (fields ? unmappedRequired(mapping, fields) : []),
    [mapping, fields],
  );
  const canSave = fields && dupes.length === 0 && missingRequired.length === 0 && !saving;
  const canContinue =
    job.status === "MAPPED" && !dirty && !saving && !validating;

  const handleChange = (next: Record<string, string>) => {
    setDirty(true);
    setConflict(false);
    setServerError(null);
    setMapping(next);
  };

  const doSave = async () => {
    if (!fields) return;
    // Strip ignored columns before sending (backend also strips, but sending
    // a smaller payload keeps the audit diff cleaner).
    const payload: Record<string, string> = {};
    for (const [k, v] of Object.entries(mapping)) {
      if (v && v !== IGNORE_TARGET) payload[k] = v;
    }
    setSaving(true);
    setServerError(null);
    try {
      await api.post(`/import/jobs/${job.id}/mapping`, {
        mapping: payload,
        if_version: job.version,
      });
      toast.success("Mapping saved");
      setDirty(false);
      onJobUpdated();
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        setConflict(true);
        onJobUpdated();
      } else {
        const parsed = parseMappingError(e);
        if (parsed) {
          const label =
            parsed.code === "duplicate_mapping_target"
              ? `Duplicate target: ${(parsed.targets || []).join(", ")}`
              : parsed.code === "missing_required_field"
              ? `Required field(s) missing: ${(parsed.fields || []).join(", ")}`
              : parsed.code === "invalid_mapping_target"
              ? `Unknown target field(s): ${(parsed.unknown || []).join(", ")}`
              : parsed.code;
          setServerError(label);
        } else {
          toastError(e, "Could not save the mapping.");
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const doValidate = async () => {
    setValidating(true);
    try {
      await api.post(`/import/jobs/${job.id}/validate`);
      onJobUpdated();
    } catch (e) {
      toastError(e, "Could not start validation.");
    } finally {
      setValidating(false);
    }
  };

  if (fieldsError) {
    return (
      <div
        role="alert"
        className="rounded-md border border-rose-200 bg-rose-50 p-4"
        data-testid="mapping-field-dict-error"
      >
        <div className="text-sm font-medium text-rose-900">
          Could not load the field dictionary
        </div>
        <div className="text-sm text-rose-800/80 mt-0.5">
          Refresh the page to try again.
        </div>
      </div>
    );
  }

  if (!fields) {
    return (
      <div className="text-sm text-slate-500" data-testid="step-3-loading">
        Loading target fields…
      </div>
    );
  }

  if (headers.length === 0) {
    return (
      <div
        role="alert"
        className="rounded-md border border-amber-200 bg-amber-50 p-4"
        data-testid="mapping-no-headers"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
          <div>
            <div className="text-sm font-medium text-amber-900">
              No CSV headers detected yet
            </div>
            <div className="text-sm text-amber-800/80 mt-0.5">
              Analyze the uploaded file first. Return to Upload and click{" "}
              <span className="font-medium">Continue to mapping</span>.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="step-3-mapping">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Map your columns</h2>
        <p className="text-sm text-slate-500 mt-1">
          Match each CSV column to a Contact field. Suggestions come from your
          headers; you can override any of them or ignore a column.
        </p>
      </div>

      {conflict && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="mapping-version-conflict"
        >
          <RefreshCw className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">This job changed elsewhere</div>
            <div className="mt-0.5 text-amber-800/80">
              The job was updated by another action or session. Your edits are
              preserved; review and save again to overwrite with the latest version.
            </div>
          </div>
        </div>
      )}

      {serverError && (
        <div
          role="alert"
          className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900"
          data-testid="mapping-server-error"
        >
          {serverError}
        </div>
      )}

      <HeadersMappingTable
        headers={headers}
        sampleRows={sampleRows}
        suggested={suggested}
        mapping={mapping}
        fields={fields}
        onChange={handleChange}
        disabled={saving}
      />

      <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
        <Button
          onClick={doSave}
          disabled={!canSave || !dirty}
          data-testid="mapping-save-btn"
          className="rounded-full"
        >
          {saving ? "Saving…" : dirty ? "Save mapping" : "Saved"}
        </Button>
        <Button
          variant="outline"
          onClick={doValidate}
          disabled={!canContinue}
          data-testid="mapping-continue-btn"
          className="rounded-full"
        >
          {validating ? "Starting review…" : "Continue to review"}
        </Button>
        <span className="text-xs text-slate-500 ml-2">
          {dirty
            ? "You have unsaved changes."
            : job.status === "MAPPED"
            ? "Mapping saved. You can continue to the review step."
            : "Save the mapping to continue."}
        </span>
      </div>
    </div>
  );
}
